import React from 'react';
import type { CanvasSectionBlock } from '../sectionRegistry';
import {
  resolveDecorativeAssetBlendMode,
  resolveDecorativeAssetFit,
} from '../../../lib/decorativeAssets';

type WrapperProps = React.HTMLAttributes<HTMLDivElement> & {
  style?: React.CSSProperties;
};

type DecorativeAssetBlockRendererProps = {
  block: CanvasSectionBlock;
  blockWrapperProps: WrapperProps;
  selectableBlockHitProps: Record<string, string>;
  selectionTarget: string;
  renderSelectionOverlay: (block: CanvasSectionBlock) => React.ReactNode;
};

const finiteNumber = (value: unknown, fallback: number) => {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export function DecorativeAssetBlockRenderer({
  block,
  blockWrapperProps,
  selectableBlockHitProps,
  selectionTarget,
  renderSelectionOverlay,
}: DecorativeAssetBlockRendererProps) {
  const content =
    block.content && typeof block.content === 'object'
      ? (block.content as Record<string, unknown>)
      : {};
  const style =
    block.style && typeof block.style === 'object'
      ? (block.style as Record<string, unknown>)
      : {};
  const assetSrc = typeof content.assetSrc === 'string' ? content.assetSrc.trim() : '';
  const isDecorative = content.decorative !== false;
  const assetAlt = isDecorative
    ? ''
    : typeof content.assetAlt === 'string'
      ? content.assetAlt
      : 'Decorative asset';
  const fit = resolveDecorativeAssetFit(content.fit);
  const opacity = Math.min(1, Math.max(0, finiteNumber(style.opacity, 1)));
  const rotation = finiteNumber(style.rotation, 0);
  const blur = Math.max(0, finiteNumber(style.blur, 0));
  const flipX = style.flipX === true;
  const flipY = style.flipY === true;
  const blendMode = resolveDecorativeAssetBlendMode(style.blendMode);
  const objectFit =
    fit === 'cover' ? 'cover' : fit === 'fill' ? 'fill' : 'contain';

  const assetStyle: React.CSSProperties = {
    opacity,
    mixBlendMode: blendMode,
    transform: `rotate(${rotation}deg) scale(${flipX ? -1 : 1}, ${flipY ? -1 : 1})`,
    filter: blur > 0 ? `blur(${blur}px)` : undefined,
  };

  return (
    <div key={block.id} {...blockWrapperProps}>
      <div
        {...(selectionTarget !== 'content' ? selectableBlockHitProps : {})}
        className="relative flex h-full w-full items-center justify-center overflow-visible"
      >
        {assetSrc ? (
          <img
            src={assetSrc}
            alt={assetAlt}
            data-builder-editor-bounds="content"
            {...(selectionTarget === 'content' ? selectableBlockHitProps : {})}
            aria-hidden={isDecorative ? true : undefined}
            className="h-full w-full select-none"
            style={{ ...assetStyle, objectFit }}
            loading="lazy"
            draggable={false}
          />
        ) : (
          <div
            data-builder-editor-bounds="content"
            {...(selectionTarget === 'content' ? selectableBlockHitProps : {})}
            className="flex h-full w-full items-center justify-center rounded-lg border border-dashed border-stone-300/70 bg-stone-50/70 px-3 py-2 text-center text-xs text-stone-500"
          >
            Decorative asset
          </div>
        )}
        {renderSelectionOverlay(block)}
      </div>
    </div>
  );
}
